import { Box } from "@mui/material";
import { DataManagementLayout } from "@pages/data-management/data-management-layout";
import { useEffect, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { useCoreApiClient } from "@api/use-core-api-client";
import { useAppParams } from "@router/router-helper";
import { fetchProjectDetails } from "@store/projects/projects-slice-thunk";
import { AuthenticatedRoute } from "@router/authenticated-route";
import {
  isProjectArchiveSelector,
  selectedProjectSelector,
} from "@store/projects/projects-selector";
import { ForbiddenPage } from "@pages/forbidden-page";
import { useDataManagement } from "@hooks/data-management/use-data-management";
import { useMembers } from "@hooks/use-members";
import { DataManagementWorkflow } from "@pages/data-management/data-management-workflow";
import { DataManagementProvider } from "@context-providers/data-management/data-management-context";
import { BaseProjectIdProps } from "@custom-types/sdb-company-types";
import { RequiredPermissionProjectLevelName } from "@utils/permission-control/project-permission-control-types";
import { isDevModeEnabledSelector } from "@store/app/app-selector";
import { useLoadingSpinner } from "@context-providers/loading-spinner-provider";
import { useAppNavigation } from "@hooks/navigation/use-app-navigation";
import { ApiClient, APITypes } from "@stellar/api-logic";
import { setHasUnacknowledgedError, setPreviousWorkflowState } from "@store/ui/ui-slice";
import { setFetchStatusCaptureTree } from "@store/capture-tree/capture-tree-slice";
import { FetchingStatus } from "@store/store-types";
import { fetchStatusCaptureTreeSelector } from "@store/capture-tree/capture-tree-selectors";

/** Renders project data management page */
export function ProjectDataManagement({ projectId }: BaseProjectIdProps): JSX.Element {
  const dispatch = useAppDispatch();
  const coreApiClient = useCoreApiClient();
  const { companyId } = useAppParams();
  const project = useAppSelector(selectedProjectSelector);
  const isProjectArchived = useAppSelector(isProjectArchiveSelector(project?.id ?? ""));
  useDataManagement({ projectId });
  const { companyMembers, projectMembers } = useMembers();
  const isDevModeEnabled = useAppSelector(isDevModeEnabledSelector);
  // loading spinner while fetching project details from CoreAPI
  const { isLoadingSpinnerShowing, setLoadingSpinner } = useLoadingSpinner();
  const { navigateToProjectDetail } = useAppNavigation();

  // ##### Fetch status ##### //

  const loadProjectDetails = useCallback(
    async (coreApiClient: ApiClient,
      companyId: string,
      projectId: APITypes.ProjectId
    ): Promise<void> => {
        try {
          await dispatch(fetchProjectDetails({ coreApiClient, companyId, projectId })).unwrap();
        } catch {
          if (isLoadingSpinnerShowing) {
            setLoadingSpinner(false);
          }
          // Error already handled in projects-slice-thunk.ts
          // Navigate to projectDetails page.
          navigateToProjectDetail({
            companyId,
            projectId,
          });
        }
      }, [dispatch, isLoadingSpinnerShowing, navigateToProjectDetail, setLoadingSpinner]
  );

  // Fetches project details.
  useEffect(() => {
    if (companyId && !project) {
      void loadProjectDetails(coreApiClient, companyId, projectId);
    }
  }, [companyId, coreApiClient, loadProjectDetails, project, projectId]);

  const fetchStatusCaptureTree = useAppSelector(fetchStatusCaptureTreeSelector);

  // Check if capture tree data is being fetched.
  const isFetchingCaptureTree = fetchStatusCaptureTree === FetchingStatus.uninitialized ||
    fetchStatusCaptureTree === FetchingStatus.pending;
  const hasFetchingError = fetchStatusCaptureTree === FetchingStatus.rejected;

  // Show the spinner until all revisions and the capture tree for main/draft have been fetched.
  // Disable the spinner afterwards or if an error is thrown.
  useEffect(() => {
    if (!project || isFetchingCaptureTree) {
      setLoadingSpinner(true);
    } else {
      setLoadingSpinner(false);
    }
  }, [isFetchingCaptureTree, project, setLoadingSpinner]);

  // Reset the Data Management UI state when navigating away from the page, since the UI state is project-specific.
  // Resetting on unmount avoids timing issues, which we got when resetting on project change instead.
  useEffect(() => {
    // Component mounted: NOP
    // Component unmounted:
    return () => {
      dispatch(setHasUnacknowledgedError(false));
      dispatch(setPreviousWorkflowState(undefined));
      // Avoid that ErrorView is shown if the user navigates like this (in a project with error):
      // Data Management -> Project Details -> Data Management
      // ...so it's consistent with the first load of the Data Management page.
      dispatch(setFetchStatusCaptureTree(FetchingStatus.uninitialized));
    };
  }, [dispatch]);

  if (isProjectArchived && !isDevModeEnabled) {
    return <ForbiddenPage />;
  }

  return (
    <AuthenticatedRoute
      key={"data-management-page"}
      requiredPermissionProjectLevel={
        isDevModeEnabled ? undefined : RequiredPermissionProjectLevelName.canViewDataManagement
      }
    >
      <DataManagementProvider
        projectId={projectId}
        companyMembers={companyMembers}
        projectMembers={projectMembers}
      >
        <DataManagementLayout>
          <Box
            data-testid="sa-container"
            sx={{ width: "100%" }}
          >
            {(!!project && !isFetchingCaptureTree) &&
              <DataManagementWorkflow project={project} hasFetchingError={hasFetchingError} />
            }
          </Box>
        </DataManagementLayout>
      </DataManagementProvider>
    </AuthenticatedRoute>
  );
}
